import React, { useState } from 'react';
import ModalBase from 'components/Tvendo/Common/ModalBase';
import Button from 'components/CustomButtons/Button.js';
import { RotateRight } from '@material-ui/icons';

/** Tope del lado largo de la foto, en px. */
const LADO = 340;

/**
 * Las dos fotos de una visita, lado a lado.
 *
 * El listado las muestra en miniaturas de 38px, que alcanzan para saber que
 * están cargadas pero no para comparar el antes y el después, que es para lo
 * que se toman. Acá van grandes y a la misma altura.
 *
 * Es solo lectura: no sube ni reemplaza nada, por eso hideSave.
 */

/**
 * Una foto con su botón para enderezarla.
 *
 * Casi siempre llegan acostadas: la app toma la miniatura que devuelve la
 * cámara (`extras.get("data")`), que es un Bitmap ya decodificado y sin EXIF,
 * así que la orientación se pierde en la captura y el servidor no tiene con
 * qué corregirla. El giro es solo para mirar; no se guarda en ningún lado.
 *
 * El recuadro se calcula con el tamaño real de la imagen, que se conoce recién
 * en el onLoad. Con medidas fijas no se puede: girada 90° el alto y el ancho se
 * intercambian, y cualquier valor que se elija a mano deja borde gris de un
 * lado o recorta del otro.
 */
function FotoConGiro({ url, titulo }) {
  const [giro, setGiro] = useState(0);
  const [natural, setNatural] = useState(null);

  const acostada = giro % 180 !== 0;

  // Escalado al lado largo. Puede agrandar: son miniaturas de cámara y a
  // tamaño natural quedan chicas para comparar.
  const escala = natural ? LADO / Math.max(natural.w, natural.h) : 1;
  const ancho = natural ? Math.round(natural.w * escala) : null;
  const alto = natural ? Math.round(natural.h * escala) : null;

  // Girada, la caja que ocupa en pantalla es la de la imagen al revés.
  const caja = natural
    ? { w: acostada ? alto : ancho, h: acostada ? ancho : alto }
    : { w: '100%', h: 200 };

  return (
    <div style={{ flex: '1 1 0', minWidth: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          marginBottom: 4,
        }}
      >
        <span style={{ fontWeight: 500 }}>{titulo}</span>

        {url && (
          <Button
            justIcon
            round
            simple
            color="info"
            title="Girar 90° a la derecha"
            onClick={() => setGiro((g) => (g + 90) % 360)}
          >
            <RotateRight />
          </Button>
        )}
      </div>

      {url ? (
        // Abre la original en otra pestaña: el modal la muestra escalada y
        // para mirar un detalle hace falta el tamaño real.
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          style={{
            display: 'block',
            position: 'relative',
            width: caja.w,
            height: caja.h,
            margin: '0 auto',
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          <img
            src={url}
            alt={titulo}
            onLoad={(e) =>
              setNatural({
                w: e.target.naturalWidth,
                h: e.target.naturalHeight,
              })
            }
            style={{
              display: 'block',
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: ancho || 'auto',
              height: alto || 'auto',
              // El translate va antes del rotate: primero centra, después gira
              // sobre ese centro. Al revés se desplazaría en diagonal.
              transform: `translate(-50%, -50%) rotate(${giro}deg)`,
              transition: 'transform .2s',
            }}
          />
        </a>
      ) : (
        <div
          style={{
            height: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 4,
            background: '#f5f5f5',
            color: '#999',
          }}
        >
          Sin foto
        </div>
      )}
    </div>
  );
}

export default function CompararFotosModal(props) {
  const { model, setOpen } = props;

  return (
    <ModalBase
      model={model}
      setOpen={setOpen}
      hideSave
      fullWidth
      maxWidth="md"
      title={`Visita del ${model?.visit_date || ''}`}
    >
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <FotoConGiro url={model?.found_file_url} titulo="Cómo lo encontró" />
        <FotoConGiro url={model?.left_file_url} titulo="Cómo lo dejó" />
      </div>

      {model?.notes && (
        <div style={{ marginTop: 16 }}>
          <b>Observación:</b> {model.notes}
        </div>
      )}
    </ModalBase>
  );
}
